import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { AlertCircle, TriangleAlert } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { KpiTile } from '../components/kpi-tile'
import { RevenueTrendCard } from '../components/revenue-trend-card'
import { ChannelMixCard } from '../components/channel-mix-card'
import { WeekdayHourHeatmap } from '../components/weekday-hour-heatmap'
import { DaypartBarsCard } from '../components/daypart-bars-card'
import { GroupListCard } from '../components/group-list-card'
import { ForecastTile } from '../components/forecast-tile'
import { MoreReportsList } from '../components/more-reports-list'
import { forecastEndOfDay } from '../lib/forecast'
import type {
  ReportMeasure,
  ReportTotals,
} from '@/features/reports/api/use-sales-report-query'
import type { ReportFilters } from '@/features/reports/lib/report-filters'
import type { PeriodPreset } from '@/features/reports/lib/period'
import type { CustomRange } from '@/features/reports/components/period-switcher'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Card, CardContent } from '@/components/ui/card'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { PermissionGate } from '@/shared/components/permission-gate'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'
import { useLocationStore } from '@/shared/store/location-store'
import { PeriodSwitcher } from '@/features/reports/components/period-switcher'
import { PeriodPresets } from '@/features/reports/components/period-presets'
import { ReportFilterBar } from '@/features/reports/components/report-filter-bar'
import { MeasureSwitcher } from '@/features/reports/components/measure-switcher'
import { CaveatNotes } from '@/features/reports/components/caveat-notes'
import { useSalesReportQuery } from '@/features/reports/api/use-sales-report-query'
import { defaultFilters } from '@/features/reports/lib/report-filters'
import {
  comparisonWindow,
  describeRange,
  describeResolvedRange,
  reportDayOf,
  resolvePeriod,
} from '@/features/reports/lib/period'
import {
  reportDate,
  reportHour,
  reportHourFraction,
  shiftReportDate,
} from '@/features/reports/lib/report-zone'
import {
  formatCurrency,
  formatDate,
  formatNumber,
} from '@/features/reports/lib/format'
import { formatDayKey } from '@/features/reports/lib/dimension-labels'

/**
 * The dashboard (CHG-2026-09-manager-reporting).
 *
 * Sections follow the questions the owner actually asks, in his order: are we growing → when do we
 * earn → where does the revenue come from → what sells → what did we get paid with. The Edge-Hub
 * and bundle cards this page used to consist of are gone: they answer an operator's question, not
 * the owner's, and both remain on their own pages (Edge-Hubs, Bundles).
 *
 * "Heute" is a state of this page, not a second page. Three things change: the trend becomes
 * hourly with a "now" marker, every comparison switches to the same weekday up to the same time of
 * day, and the fourth tile becomes an end-of-day estimate. The heatmap is dropped, because for a
 * single day it is one row.
 *
 * Every section is the **same** endpoint with a different dimension, which is why a tile and the
 * table it drills into can never disagree.
 */
export function DashboardPage({
  // The running day is what somebody opening the backoffice wants to know first: how is it going
  // right now. A fortnight is the answer to a different question, and it was the one nobody asked.
  period = 'today',
}: {
  period?: PeriodPreset
}) {
  return (
    <PermissionGate action={ACTIONS.OVERVIEW_VIEW}>
      <DashboardContent period={period} />
    </PermissionGate>
  )
}

function DashboardContent({ period }: { period: PeriodPreset }) {
  const { t } = useTranslation('reports')
  const { t: tDashboard } = useTranslation('dashboard')
  const { can, isLoading: permissionsLoading } = usePermissions()
  const restaurantIds = useLocationStore((s) => s.restaurantIds)

  const [preset, setPreset] = useState<PeriodPreset>(period)
  const [customRange, setCustomRange] = useState<CustomRange | null>(null)
  const [measure, setMeasure] = useState<ReportMeasure>('revenue')
  const [groupLevel, setGroupLevel] = useState<
    'category_main' | 'category_sub'
  >('category_main')

  // One way in for both period controls, so the quick picks and the picker cannot drift apart.
  const applyPeriod = (next: PeriodPreset, range?: CustomRange) => {
    setPreset(next)
    if (range) setCustomRange(range)
  }

  const isToday = preset === 'today'
  const now = useRunningClock(isToday)
  const today = reportDate(now)

  const filters: ReportFilters = {
    ...defaultFilters(restaurantIds),
    preset,
    customFrom: customRange?.from,
    customTo: customRange?.to,
    customFromTime: customRange?.fromTime,
    customToTime: customRange?.toTime,
    customGrain: customRange?.grain,
    restaurantIds,
  }

  // The dashboard is the revenue report in tile form, so it follows that permission — not the
  // one that governs individual receipts.
  const canSeeRevenue = can(ACTIONS.REPORT_SALES_VIEW)
  const enabled = canSeeRevenue && !permissionsLoading

  const trend = useSalesReportQuery(
    {
      ...filters,
      dimension: isToday ? 'time_hour' : 'time_day',
      measure,
      limit: 1000,
    },
    enabled,
  )
  // The finished days behind today, hour by hour, in one request — what the end-of-day estimate
  // reads the shape of a day from. The trend's own comparison cannot serve: the running window
  // ends at "now" and the backend shifts that same window back, so its previous side stops at the
  // current hour and carries nothing about the hours still to come.
  //
  // A whole-day window from a bare date, so the string holds no "now" and the answer is fetched
  // once and cached for the session. Hourly buckets over four weeks are at most 672 rows whatever
  // the location filter says, comfortably inside the endpoint's cap of 1000.
  const referenceDays = useSalesReportQuery(
    {
      ...filters,
      preset: 'custom',
      customFrom: shiftReportDate(today, -REFERENCE_WINDOW_DAYS),
      customTo: shiftReportDate(today, -1),
      customFromTime: undefined,
      customToTime: undefined,
      customGrain: undefined,
      dimension: 'time_hour',
      measure,
      limit: 1000,
    },
    enabled && isToday,
  )
  const channels = useSalesReportQuery(
    { ...filters, dimension: 'channel', measure },
    enabled,
  )
  const heatmap = useSalesReportQuery(
    { ...filters, dimension: 'weekday_hour', measure, limit: 1000 },
    enabled && !isToday,
  )
  // The same window restricted to the staffed channel, so the heatmap can say where the unattended
  // tail of the day actually begins at this location instead of assuming an hour.
  const staffedHeatmap = useSalesReportQuery(
    {
      ...filters,
      dimension: 'weekday_hour',
      measure,
      channels: ['STAFF'],
      limit: 1000,
    },
    enabled && !isToday,
  )
  const dayparts = useSalesReportQuery(
    { ...filters, dimension: 'daypart', measure },
    enabled,
  )
  const groups = useSalesReportQuery(
    { ...filters, dimension: groupLevel, measure },
    enabled,
  )
  const payments = useSalesReportQuery(
    { ...filters, dimension: 'payment_method', measure },
    enabled,
  )

  const resolved = resolvePeriod(preset, now, customRange ?? undefined)
  const comparison = comparisonWindow(resolved, now)
  const isSingleDay = resolved.days === 1

  // A single day carries two comparisons, because either one alone invites the wrong conclusion:
  // the day before answers "wie läuft es gerade", the same weekday last week answers "ist das ein
  // guter Freitag". A strong Friday against a weak Thursday says nothing about the trade. The
  // period already picks one; this asks the same window the other question.
  //
  // `channel` rather than the trend's own dimension: the endpoint's totals cover the whole window
  // regardless of how it is broken down, and this query wants nothing but `previous`. The trend's
  // dimension would refetch up to a thousand hourly rows to reach the same four numbers.
  const otherComparison =
    resolved.comparison === 'same_weekday_last_week'
      ? 'previous_period'
      : 'same_weekday_last_week'
  const otherTrend = useSalesReportQuery(
    { ...filters, dimension: 'channel', measure, comparison: otherComparison },
    enabled && isSingleDay,
  )

  // The two windows read in a fixed order — the day before first, the same weekday under it —
  // rather than in whichever order the period happens to produce them. The running day asks for
  // the weekday comparison itself and a picked day asks for the previous period, so without this
  // the same two lines would swap places depending on how the day was chosen.
  const dayBeforeFromPeriod = resolved.comparison === 'previous_period'
  const dayBefore = dayBeforeFromPeriod
    ? trend.data?.previous
    : otherTrend.data?.previous
  const sameWeekday = dayBeforeFromPeriod
    ? otherTrend.data?.previous
    : trend.data?.previous
  const dayOf = (shift: number) =>
    formatDayKey(shiftReportDate(reportDayOf(resolved.from), shift))

  // A day names the day it is measured against; only a span falls back to "Vorperiode", where
  // spelling out both ends would be longer than the figure it sits under.
  const againstLabel = isSingleDay
    ? t('delta.againstDay', { day: dayOf(-1) })
    : t('delta.againstPreviousPeriod')
  const otherAgainstLabel = t('delta.againstDay', { day: dayOf(-7) })

  /** One figure's pair of deltas, or the period's single one when it is not a day. */
  const deltas = (
    figure: (totals: ReportTotals) => number | null,
  ): {
    previous: number | null | undefined
    secondary?: { previous: number | null; againstLabel: string }
  } => {
    if (!isSingleDay) {
      return { previous: previous ? figure(previous) : null }
    }
    return {
      previous: dayBefore ? figure(dayBefore) : null,
      secondary: sameWeekday
        ? {
            previous: figure(sameWeekday),
            againstLabel: otherAgainstLabel,
          }
        : undefined,
    }
  }

  const total = trend.data?.total
  const previous = trend.data?.previous
  const hourly = (trend.data?.rows ?? []).map((row) => ({
    key: row.key,
    revenueCents: row.revenueCents,
  }))
  const referenceHourly = (referenceDays.data?.rows ?? []).map((row) => ({
    key: row.key,
    revenueCents: row.revenueCents,
  }))
  const forecast = forecastEndOfDay({
    today,
    hours: hourly,
    referenceHours: referenceHourly,
    hour: reportHour(now),
    hourFraction: reportHourFraction(now),
  })

  const collectorRow = (groups.data?.rows ?? []).find((r) => r.isCollector)

  // What the collapsed filter button says on a phone: the same facts the inline controls show.
  const filterSummary = [
    preset === 'custom' && customRange
      ? describeRange(t, customRange)
      : t(`period.presets.${preset}`),
  ].join(' · ')

  // A tile's drill-through hands the report page this page's own slice — dimension, measure and
  // period — so the table it opens shows exactly what the tile showed rather than a default.
  const drillSearch = (dimension: string) => ({
    dimension,
    measure,
    period: preset,
    // A picked range has to travel with the preset, or the report page would fall back to a
    // default window and show different numbers than the tile that was clicked.
    ...(preset === 'custom' && customRange
      ? {
          from: customRange.from,
          to: customRange.to,
          fromTime: customRange.fromTime,
          toTime: customRange.toTime,
          grain: customRange.grain,
        }
      : {}),
  })

  if (!permissionsLoading && !canSeeRevenue) {
    // A role with the dashboard but not the sales permission is told so once, rather than shown a
    // wall of "forbidden" errors from six requests it was never allowed to make.
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">{tDashboard('title')}</h1>
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {t('noRevenuePermission')}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold leading-9">
            {tDashboard('title')}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isToday
              ? t('period.runningDay', { day: formatDayKey(today) })
              : preset === 'custom' && customRange
                ? describeRange(t, customRange)
                : describeResolvedRange(t, resolved)}
          </p>
        </div>
        {/* The Tageszeit filter is deliberately absent here, and only here: two cards below
            already answer "wann verdienen wir" — the Tageszeit bars and the weekday × hour
            heatmap — so narrowing the whole dashboard to 11:00–14:00 would leave those two showing
            one bar and one band. On the reports it stays: "welche Artikel verkaufen wir mittags,
            im September" has no other way to be asked.

            One filter row above everything it scopes — never a filter inside a chart card. The
            measure stays outside the collapsing bar: switching Umsatz/Stück is the one control the
            owner reaches for constantly, and burying it behind a sheet would cost a tap every time. */}
        <div className="flex flex-wrap items-center gap-2 lg:flex-nowrap">
          <div className="w-full min-w-0 lg:w-auto">
            <ReportFilterBar summary={filterSummary}>
              {/* The four lengths sit beside the picker, not behind it: "wie laufen die
                  letzten 7 Tage" is what this page is opened for, and folding them into the
                  picker's quick list made it cost two clicks. Inside the bar rather than next
                  to it, so the phone gets them in the same sheet as the picker instead of a
                  second row above the first figure. */}
              <PeriodPresets value={preset} onChange={setPreset} />
              <PeriodSwitcher
                value={preset}
                custom={customRange}
                onChange={applyPeriod}
                // The Tageszeit filter is not on this page (see below), so the picker must not
                // send the reader looking for it.
                hasTimeWindowFilter={false}
                // The presets beside it state the period; this one is the way to anything they
                // cannot say.
                hasPresetsBeside
              />
            </ReportFilterBar>
          </div>
          <MeasureSwitcher value={measure} onChange={setMeasure} />
        </div>
      </div>

      {isToday ? (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {/* No longer names one window: the tiles now show two, and the point of the
                sentence is the truncation, which is true of both. */}
            {t('period.runningDayNote')}
          </AlertDescription>
        </Alert>
      ) : null}

      {trend.error ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{tDashboard('errorTitle')}</AlertTitle>
          <AlertDescription>{tDashboard('errorDescription')}</AlertDescription>
        </Alert>
      ) : null}

      {/* Refetch keeps the frame: the previous render stays at reduced opacity instead of
          collapsing into skeletons and jumping the layout. */}
      <div
        className={
          trend.isFetching && trend.data
            ? 'opacity-60 transition-opacity'
            : undefined
        }
      >
        <div className="grid gap-4 lg:grid-cols-4">
          {/* Revenue leads, inverted and full width on a phone: it is the figure the page exists
              for, and four identical cards make the reader rank them himself every time. The three
              supporting figures share one row below it; `lg:contents` dissolves that row back into
              the four-column grid on a desktop, so the wrapper exists only where it is needed. */}
          <div>
            <KpiTile
              variant="hero"
              label={t('kpi.revenue')}
              value={formatCurrency(total?.revenueCents ?? 0)}
              current={total?.revenueCents ?? 0}
              {...deltas((totals) => totals.revenueCents)}
              againstLabel={againstLabel}
              caption={
                isToday
                  ? undefined
                  : t('period.comparisonWindow', {
                      from: formatDate(`${comparison.from}T12:00:00Z`),
                      to: formatDate(`${comparison.to}T12:00:00Z`),
                    })
              }
              loading={trend.isLoading}
            />
          </div>
          <div className="grid grid-cols-3 gap-4 lg:contents">
            <KpiTile
              label={t('kpi.bons')}
              value={formatNumber(total?.bons ?? 0)}
              current={total?.bons ?? 0}
              {...deltas((totals) => totals.bons)}
              againstLabel={againstLabel}
              loading={trend.isLoading}
            />
            <KpiTile
              label={t('kpi.averageBon')}
              value={formatCurrency(average(total?.revenueCents, total?.bons))}
              current={average(total?.revenueCents, total?.bons)}
              {...deltas((totals) => average(totals.revenueCents, totals.bons))}
              againstLabel={againstLabel}
              loading={trend.isLoading}
            />
            {isToday ? (
              <ForecastTile
                forecast={forecast}
                loading={trend.isLoading || referenceDays.isLoading}
              />
            ) : (
              <KpiTile
                label={t('kpi.itemsPerBon')}
                value={ratio(total?.units, total?.bons)}
                current={total?.bons ? (total.units / total.bons) * 100 : 0}
                {...deltas((totals) =>
                  totals.bons ? (totals.units / totals.bons) * 100 : null,
                )}
                againstLabel={againstLabel}
                loading={trend.isLoading}
              />
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <RevenueTrendCard
          rows={trend.data?.rows ?? []}
          measure={measure}
          granularity={isToday ? 'hour' : 'day'}
          runningKey={isToday ? currentHourKey(today, now) : today}
          comparisonLabel={
            isToday ? t('trend.typicalDay') : t('delta.againstPreviousPeriod')
          }
          loading={trend.isLoading}
        />
        <ChannelMixCard
          rows={channels.data?.rows ?? []}
          total={channels.data?.total ?? EMPTY_TOTALS}
          measure={measure}
          detailsTo={REPORTS_SALES_PATH}
          detailsSearch={drillSearch('channel')}
          loading={channels.isLoading}
        />
      </div>

      {/* The heatmap is a fortnight's shape; for a single day it would be one row, so the running
          day shows the daypart bars instead. On mobile the bars replace it at every period. */}
      {isToday ? (
        <DaypartBarsCard
          rows={dayparts.data?.rows ?? []}
          total={dayparts.data?.total ?? EMPTY_TOTALS}
          measure={measure}
          detailsTo={REPORTS_SALES_PATH}
          detailsSearch={drillSearch('daypart')}
          loading={dayparts.isLoading}
        />
      ) : (
        <>
          <div className="hidden lg:block">
            <WeekdayHourHeatmap
              rows={heatmap.data?.rows ?? []}
              staffedRows={staffedHeatmap.data?.rows}
              measure={measure}
              loading={heatmap.isLoading}
            />
          </div>
          <div className="lg:hidden">
            <DaypartBarsCard
              rows={dayparts.data?.rows ?? []}
              total={dayparts.data?.total ?? EMPTY_TOTALS}
              measure={measure}
              detailsTo={REPORTS_SALES_PATH}
              detailsSearch={drillSearch('daypart')}
              loading={dayparts.isLoading}
            />
          </div>
        </>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <GroupListCard
          title={t('groups.title')}
          subtitle={t('groups.subtitle')}
          dimension={groupLevel}
          showSecondaryMeasure
          rows={groups.data?.rows ?? []}
          total={groups.data?.total ?? EMPTY_TOTALS}
          measure={measure}
          detailsTo={REPORTS_SALES_PATH}
          detailsSearch={drillSearch(groupLevel)}
          loading={groups.isLoading}
          headerAction={
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={groupLevel}
              onValueChange={(next) => {
                if (next)
                  setGroupLevel(next as 'category_main' | 'category_sub')
              }}
              aria-label={t('dimension.levelLabel')}
            >
              <ToggleGroupItem value="category_main">
                {t('dimension.levels.category_main_short')}
              </ToggleGroupItem>
              <ToggleGroupItem value="category_sub">
                {t('dimension.levels.category_sub_short')}
              </ToggleGroupItem>
            </ToggleGroup>
          }
          footer={
            collectorRow ? (
              <div className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-xs leading-5">
                <TriangleAlert
                  className="mr-1.5 inline h-3.5 w-3.5 text-warning"
                  aria-hidden="true"
                />
                {t('groups.collectorWarning', { count: collectorRow.skus })}{' '}
                <Link to="/categories" className="underline">
                  {t('groups.collectorAction')}
                </Link>
              </div>
            ) : null
          }
        />
        <GroupListCard
          title={t('paymentMethods.title')}
          subtitle={t('paymentMethods.subtitle')}
          dimension="payment_method"
          rows={payments.data?.rows ?? []}
          total={payments.data?.total ?? EMPTY_TOTALS}
          measure={measure}
          detailsTo={REPORTS_SALES_PATH}
          detailsSearch={drillSearch('payment_method')}
          loading={payments.isLoading}
        />
      </div>

      <MoreReportsList
        entries={[
          {
            to: REPORTS_SALES_PATH,
            search: drillSearch('payment_method'),
            label: t('paymentMethods.title'),
          },
          // Dropped for a role that may not open it — a dead link is worse than a missing one.
          ...(can(ACTIONS.REPORT_ARTICLES_VIEW)
            ? [{ to: '/reports/articles', label: t('articles.title') }]
            : []),
          {
            to: '/categories',
            label: t('groups.collector'),
            badge: collectorRow ? String(collectorRow.skus) : undefined,
          },
        ]}
      />

      <CaveatNotes caveats={trend.data?.caveats ?? []} />
    </div>
  )
}

const EMPTY_TOTALS = { revenueCents: 0, units: 0, bons: 0, skus: 0 }

/** The one report surface every tile drills into. */
const REPORTS_SALES_PATH = '/reports/sales'

function average(
  revenueCents: number | undefined,
  bons: number | undefined,
): number {
  if (!revenueCents || !bons) return 0
  return Math.round(revenueCents / bons)
}

/** Two decimals, because 1,90 Artikel je Bon is a meaningful difference from 1,95. */
const ratioFormatter = new Intl.NumberFormat('de-AT', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

function ratio(units: number | undefined, bons: number | undefined): string {
  if (!units || !bons) return ratioFormatter.format(0)
  return ratioFormatter.format(units / bons)
}

/** Four weeks back: enough to hold eight comparable days even with a bank holiday in them. */
const REFERENCE_WINDOW_DAYS = 28

/**
 * How often the running day re-reads the clock. An estimate does not need the minute, and every
 * tick re-asks each tile's query for a window one tick longer.
 */
const CLOCK_TICK_MS = 5 * 60_000

/**
 * The clock the running day is read against.
 *
 * It used to be `useMemo(() => new Date(), [])` — fixed at the moment the page mounted, while the
 * window every query asks for is rebuilt from the wall clock on each render. A dashboard left open
 * through the morning went on dividing by the share of a day done at the hour somebody opened the
 * tab: Umsatz climbed, the estimate did not, and the floor at what was already taken eventually
 * pinned the tile to the revenue.
 *
 * Only the running day ticks. A finished period has nothing to tick towards, and a moving clock
 * there would refetch a window that cannot change.
 */
function useRunningClock(running: boolean): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    if (!running) return
    const tick = setInterval(() => setNow(new Date()), CLOCK_TICK_MS)
    return () => clearInterval(tick)
  }, [running])
  return now
}

/** `2026-09-04T12` — the Vienna bucket the clock is currently inside. */
function currentHourKey(today: string, now: Date): string {
  return `${today}T${String(reportHour(now)).padStart(2, '0')}`
}
